/* eslint-disable no-plusplus */
import { range } from "lodash";
import {
  emptyTextCell,
  nonEditable,
  textCell,
  bottomLine,
  showZero,
  noSideBorders,
  headerCell
} from "../cells";
import { HEADER_ROW_ID, ROW_HEIGHT } from "../constants";
import { formatCurrency, formatDecimalValues } from "@/utils/helpers";

function getHeaderRow(columns) {
  return {
    rowId: HEADER_ROW_ID,
    height: ROW_HEIGHT,
    cells: columns.map((column) => headerCell(column.label)),
  };
}

// Totals every column flagged `summable`, keyed by columnId rather than by position, so a total
// always lands under its own heading no matter how the columns are ordered or extended.
function sumGroup(groups, columns) {
  return columns
    .filter((column) => column.summable)
    .reduce((acc, column) => ({
      ...acc,
      [column.columnId]: groups.reduce(
        (sum, row) => sum + (Number(row[column.columnId]) || 0),
        0
      ),
    }), {});
}

function getGroupRows(title, groups, columns) {
  if (!groups?.length) {
    return [];
  }

  // The banner and footer rows must span exactly columns.length cells. They used to be seven
  // hardcoded cells, which silently truncated the moment a column was added or removed.
  const lastIndex = columns.length - 1;

  const bannerCells = columns.map((column, index) => {
    if (index === 0) {
      return bottomLine(nonEditable(textCell({ text: title }), "font-bold light-green-bg "));
    }

    const cell = bottomLine(nonEditable(emptyTextCell(), "light-green-bg"));
    return index < lastIndex - 1 ? noSideBorders(cell) : cell;
  });

  const totals = sumGroup(groups, columns);

  const footerCells = columns.map((column, index) => {
    if (index === 0) {
      return nonEditable(
        textCell({ text: "Group Total" }),
        "font-bold justify-content-start total-row"
      );
    }

    const total = totals[column.columnId];

    return nonEditable(
      showZero(textCell({ text: total === undefined ? "" : formatDecimalValues(total) })),
      "total-row"
    );
  });

  return [
    { rowId: `${title}Header`, height: ROW_HEIGHT, cells: bannerCells },
    ...groups.map((item, index) => ({
      // ReactGrid needs a unique rowId per row; the report carries no record key of its own,
      // so the caller synthesises `id` and the title/index pair is the fallback.
      rowId: item.id ?? `${title}-${index}`,
      height: ROW_HEIGHT,
      cells: columns.map((column) => {
        const textCellArgs = {};
        if (column.formatter) {
          textCellArgs.text = column.formatter(item[column.columnId], index);
        } else {
          textCellArgs.text = item[column.columnId] ?? "";
        }
        return nonEditable(textCell(textCellArgs), column.className || "");
      }),
    })),
    { rowId: `${title}Footer`, height: ROW_HEIGHT, cells: footerCells },
  ];
}

export function getTenantStatementRows({
  data,
  columns,
  groups = []
}) {
  return [
    getHeaderRow(columns),
    ...groups.flatMap(({ key, title }) => getGroupRows(title, data.data?.[key], columns)),
  ];
}

function getRecieptGroupRows(groups, columns, totals) {
  if (groups?.length) {
    return [
      ...groups.map((item, index) => ({
        rowId: item.id,
        height: ROW_HEIGHT,
        cells: columns.map((column) => {
          const textCellArgs = {};

          if (column.formatter) {
            textCellArgs.text = column.formatter(item[column.columnId], index);
          } else {
            textCellArgs.text = item[column.columnId] ?? "";
          }

          return nonEditable(textCell(textCellArgs), column.className || "");
        }),
      })),
      {
        rowId: "Total-Footer",
        height: ROW_HEIGHT,
        cells: [
          ...range(0, columns.length - (totals.length + 1)).map(() => headerCell("", "total-row")),
          headerCell(
            "Total", "total-row"),
          ...totals.map((total) => headerCell(formatCurrency(total || 0), "total-row total-row-credit")),
        ],
      },
    ];
  }
  return [];
}

export function getReceiptRows({
  data,
  columns
}) {

  if (!data) {
    return [];
  }

  let totalDebit = 0;
  let totalCredit = 0;
  let totalBalance = 0;
  let endingRunningBalance = 0;
  data.data?.forEach((item) => {
    totalDebit += item.debit || 0;
    totalCredit += item.credit || 0;
    totalBalance += item.balance || 0;
    endingRunningBalance = item.runningBalance ?? endingRunningBalance;
  });

  return [getHeaderRow(columns),
    ...getRecieptGroupRows(
      data.data,
      columns,
      [totalDebit, totalCredit, totalBalance, endingRunningBalance]
    )];
}

// A memo block: cheques on hand, or credit notes the ledger has not applied. Neither is a
// receivable movement, so each gets its own grid beneath the statement with its own subtotal
// rather than rows inside it — which is also what keeps them visible no matter which page of the
// statement is on screen.
export function getMemoRows({
  data,
  columns,
  totalLabel = "Total"
}) {
  const rows = data?.data ?? [];

  if (!rows.length) {
    return [];
  }

  const totals = sumGroup(rows, columns);
  const lastIndex = columns.length - 1;

  return [
    getHeaderRow(columns),
    ...rows.map((item, index) => ({
      rowId: item.id ?? `memo-${index}`,
      height: ROW_HEIGHT,
      cells: columns.map((column) => {
        const text = column.formatter ?
          column.formatter(item[column.columnId], index) :
          item[column.columnId] ?? "";

        return nonEditable(textCell({ text }), column.className || "");
      }),
    })),
    {
      rowId: "memo-footer",
      height: ROW_HEIGHT,
      cells: columns.map((column, index) => {
        if (index === 0) {
          return nonEditable(
            textCell({ text: totalLabel }),
            "font-bold justify-content-start total-row"
          );
        }

        const total = totals[column.columnId];

        // Only the summable columns carry a figure; the rest are padding so the footer spans
        // exactly columns.length cells, which ReactGrid requires.
        return nonEditable(
          showZero(textCell({ text: total === undefined ? "" : formatDecimalValues(total) })),
          index === lastIndex ? "total-row total-row-credit" : "total-row"
        );
      }),
    },
  ];
}
